import React, { Component } from 'react';
import { Route, Switch } from 'react-router-dom';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import queryString from 'query-string';
import Loading from '@jg/loading';
import ErrorPage from '@jg/error-page';
import { TrackingProvider } from '@jg/react-tracking';
import { captureException } from '@sentry/browser';
import { Trans, withTranslation } from 'react-i18next';
import Alert from '@jg/alert';
import ErrorBoundary from '@jg/error-boundary';
import { appActions, apiThunk } from './actions';
import {
  IAppComponent,
  IAppComponentMapStateToProps,
  IAppComponentDispatchToProps,
} from './App.d';
import {
  getRequestIsInFlight,
  getRequestIsError,
} from './selectors/loadingSelectors';
import { getFrpId } from './selectors/appSelectors';
import { ROUTES } from './consts';
import { IAppState } from './types/state.d';
import { getMatchedRouteParams, deleteCookie } from './utils';
import config from './config';
import {
  Story,
  FundraisingSettings,
  YourTeamPage,
  YourFundraisingCause,
  JoinedWithNewFrp,
  EditTeam,
  YouAreInTheTeam,
  Join,
  CreateTeam,
  TeamSettings,
  GiftAid,
  EditMembers,
  Settings,
} from './containers';
import EventsPage from './components/EventsPage/EventsPage';
import CreateEventPage from './containers/CreateEventPage/CreateEventPage';
import SelectCausePage from './containers/SelectCausePage/SelectCausePage';
import AdminTabs from './components/AdminTabs/AdminTabs';
import ConnectFRPWrapper from './containers/ConnectFRPWrapper/ConnectFRPWrapper';
import analytics from './utils/analytics';
import { getTeamGuid, getTeamCharityId } from './selectors/apiSelectors';

export class App extends Component<IAppComponent> {
  state = {
    noTeamDataRequired: false,
    noFrpId: false,
  };

  componentDidMount() {
    const {
      history: {
        location: { pathname, search },
      },
      saveFrpId,
      fetchAppData,
    } = this.props;
    const { frpid } = queryString.parse(search);
    const { teamGuid } = getMatchedRouteParams(pathname);

    if (frpid) {
      saveFrpId(frpid);
      this.setState({
        noTeamDataRequired: true,
      });
    } else if (pathname === ROUTES.INDEX) {
      this.setState({
        noFrpId: true,
      });
    }
    if (teamGuid) {
      fetchAppData(teamGuid);
    }
    deleteCookie(document, config.activeGroupCookieName);
  }

  onComponentDidCatch = (error: Error) => {
    captureException(error);
  };

  render() {
    const {
      isFatalError,
      isInFlight,
      t,
      frpId,
      teamGuid,
      charityId,
      errorStatus,
    } = this.props;
    const { noTeamDataRequired, noFrpId } = this.state;
    const displayLoading =
      !noTeamDataRequired && (isInFlight || isInFlight === undefined);

    const ErrorComponent = (status: number, errorMessage: string) => (
      <div
        data-qa={`error-component-${status}`}
        className="jg-space-ptmd col-md-8 offset-md-2"
      >
        <ErrorPage status={status} errorMessage={errorMessage} />
      </div>
    );

    switch (true) {
      case errorStatus === '404':
        return ErrorComponent(404, t('content.error.api.teamnotfound'));
      case errorStatus === '400':
        return ErrorComponent(400, t('content.error.api.badrequest'));
      case isFatalError:
        return ErrorComponent(500, t('content.error.api.generic'));
      case noFrpId:
        return (
          <Alert
            type="error"
            displayType="inline"
            className="jg-text--pull-left"
            data-qa="no-frpid"
          >
            <Trans i18nKey="content.error.api.generic.nofrp">
              Oops! Fundraising page not found.
              <a href={config.createFRPUri}>Create a fundraising page</a> to
              start a team
            </Trans>
          </Alert>
        );
      case displayLoading:
        return (
          <Loading size="medium" colour="purple" className="jg-space-ptlg" />
        );
      default:
        return (
          <ErrorBoundary
            fallbackContent={ErrorComponent}
            onError={this.onComponentDidCatch}
          >
            <TrackingProvider
              analytics={analytics}
              defaultTrackingData={{
                page_type: 'team page',
                content_type: 'team_page_id',
                charity_id: charityId,
                page_id: frpId,
                content_id: teamGuid,
              }}
            >
              <Switch>
                <Route path={ROUTES.EDIT_TEAM}>
                  <AdminTabs active="edit" />
                </Route>
                <Route path={ROUTES.EDIT_MEMBERS}>
                  <AdminTabs active="members" />
                </Route>
                <Route path={ROUTES.SETTINGS}>
                  <AdminTabs active="settings" />
                </Route>
              </Switch>
              <div className="container jg-space-pvsm@md jg-space-phlg@md jg-bd jg-br jg-bd--very-light-grey jg-background--white">
                <Switch>
                  <Route path={ROUTES.INDEX} component={CreateTeam} exact />
                  <Route path={ROUTES.EDIT_TEAM_NAME} component={CreateTeam} />
                  <Route
                    path={ROUTES.YOUR_FUNDRAISING_CAUSE}
                    component={YourFundraisingCause}
                  />
                  <Route path={ROUTES.STORY} component={Story} />
                  <Route path={ROUTES.TEAM_SETTINGS} component={TeamSettings} />
                  <Route
                    path={ROUTES.YOUR_TEAM_PAGE}
                    component={YourTeamPage}
                  />

                  <Route path={ROUTES.JOIN} component={Join} exact />
                  <Route
                    path={ROUTES.LINK_FUNDRAISING_PAGE}
                    component={ConnectFRPWrapper}
                  />
                  <Route
                    path={ROUTES.YOU_ARE_IN_THE_TEAM}
                    component={YouAreInTheTeam}
                  />

                  <Route path={ROUTES.CAUSE} component={SelectCausePage} />
                  <Route path={ROUTES.EVENTS} component={EventsPage} />
                  <Route
                    path={ROUTES.CREATE_EVENT}
                    component={CreateEventPage}
                  />
                  <Route
                    path={ROUTES.FUNDRAISING_SETTINGS}
                    component={FundraisingSettings}
                  />
                  <Route path={ROUTES.GIFT_AID} component={GiftAid} />
                  <Route
                    path={ROUTES.JOINED_WITH_NEW_FRP}
                    component={JoinedWithNewFrp}
                  />

                  <Route path={ROUTES.EDIT_TEAM} component={EditTeam} />
                  <Route path={ROUTES.SETTINGS} component={Settings} />
                  <Route path={ROUTES.EDIT_MEMBERS} component={EditMembers} />
                </Switch>
              </div>
            </TrackingProvider>
          </ErrorBoundary>
        );
    }
  }
}

export const mapDispatchToProps: IAppComponentDispatchToProps = {
  saveFrpId: appActions.saveFrpId,
  fetchAppData: apiThunk.fetchAppData,
};

export const mapStateToProps = (
  state: IAppState,
): IAppComponentMapStateToProps => ({
  isInFlight: getRequestIsInFlight(state, 'GET_APP_DATA'),
  isFatalError: Boolean(getRequestIsError(state, 'GET_APP_DATA')),
  charityId: getTeamCharityId(state),
  frpId: getFrpId(state),
  teamGuid: getTeamGuid(state),
  errorStatus: state.api.errorStatus,
});

export default withTranslation()(
  withRouter(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(App),
  ),
);
